import type { VatPresentation } from '$lib/money';
import type { ShopStorefront } from './storefront';
import { page } from '$app/state';
import { createContext, onMount } from 'svelte';

export type ShopVatLabels = {
	vatIncludedLabel: string;
	vatExcludedLabel: string;
	vatToggleLabel: string;
	vatIncludedSuffix: string;
	vatExcludedSuffix: string;
	vatCartNotice: string;
};

export const defaultShopVatLabels: ShopVatLabels = {
	vatIncludedLabel: 'Inclusief btw',
	vatExcludedLabel: 'Exclusief btw',
	vatToggleLabel: 'Prijsweergave',
	vatIncludedSuffix: 'incl. btw',
	vatExcludedSuffix: 'excl. btw',
	vatCartNotice: 'In je winkelmand en bij het afrekenen zie je prijzen inclusief btw.'
};

export function storefrontVat(storefront: ShopStorefront | null, url: URL): VatPresentation {
	return storefront?.kind === 'business' && url.searchParams.get('vat') !== 'incl'
		? 'excluded'
		: 'included';
}

export function vatToggleUrl(url: URL, vat: VatPresentation): URL {
	const next = new URL(url);
	next.searchParams.set('vat', vat === 'included' ? 'incl' : 'excl');
	return next;
}

/** Only carry the explicit VAT choice to another URL within this business storefront. */
export function preserveShopVat(href: string, current: URL, storefront: ShopStorefront | null) {
	const vat = current.searchParams.get('vat');
	if (storefront?.kind !== 'business' || (vat !== 'incl' && vat !== 'excl')) return href;
	const target = new URL(href, current);
	const root = storefront.archivePath;
	if (
		target.origin !== current.origin ||
		(target.pathname !== root.slice(0, -1) && !target.pathname.startsWith(root))
	)
		return href;
	target.searchParams.set('vat', vat);
	return `${target.pathname}${target.search}${target.hash}`;
}

type ShopPresentation = {
	readonly business: boolean;
	readonly ready: boolean;
	readonly vat: VatPresentation;
	readonly labels: ShopVatLabels;
	link: (href: string) => string;
};

export const inclusiveShopPresentation: ShopPresentation = {
	business: false,
	ready: true,
	vat: 'included',
	labels: defaultShopVatLabels,
	link: (href) => href
};

export const [getShopPresentation, setShopPresentation] = createContext<ShopPresentation>();

export function provideShopPresentation(
	storefront: () => ShopStorefront | null,
	labels: () => ShopVatLabels
) {
	let mounted = $state(false);
	onMount(() => {
		mounted = true;
	});
	const url = $derived.by(() => {
		// This SvelteKit version updates page.state, but not page.url, on pushState.
		// Track both kinds of navigation and always read the actual browser URL.
		page.state;
		page.url;
		return mounted ? new URL(window.location.href) : null;
	});
	const presentation: ShopPresentation = {
		get business() {
			return storefront()?.kind === 'business';
		},
		// Prerendered pages have no query preference. Hold prices until hydration resolves it.
		get ready() {
			return !this.business || mounted;
		},
		get vat() {
			return url ? storefrontVat(storefront(), url) : this.business ? 'excluded' : 'included';
		},
		get labels() {
			return labels();
		},
		link(href) {
			return url ? preserveShopVat(href, url, storefront()) : href;
		}
	};
	setShopPresentation(presentation);
	return presentation;
}
